import type { InitCache } from "../types/session.ts";
import { getDbFeatureFlags } from "./dbFeatureFlags.ts";

export interface TzOption {
  value: string;
  label: string;
}

/** IANA timezone ids (DST-aware). Empty when the runtime lacks Intl.supportedValuesOf. */
const IANA_ZONES: string[] = (() => {
  try {
    const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    return f ? f("timeZone") : [];
  } catch {
    return [];
  }
})();

/** Legacy Windows zone list from InitCache.TimeZones, labelled with UTC offset. */
function windowsZoneOptions(cache: Pick<InitCache, "TimeZones"> | null | undefined, current: string): TzOption[] {
  const tz = cache?.TimeZones as { Name?: string; Offset?: number; Abbreviation?: string }[] | undefined;
  if (!tz?.length) return [{ value: current, label: current }];
  const seen = new Set<string>();
  const result: TzOption[] = [];
  for (const t of tz) {
    const name = t.Name ?? "";
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const offsetHours = t.Offset ?? 0;
    const h = Math.trunc(offsetHours);
    const m = Math.abs(Math.round((offsetHours - h) * 60));
    const sign = offsetHours >= 0 ? "+" : "";
    result.push({ value: name, label: `${name} (${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")})` });
  }
  return result.length > 0 ? result : [{ value: current, label: current }];
}

/**
 * Options for the export "output timezone" picker, shared by the Export Grid
 * dialog and the Scheduled Export form. IANA ids when the `ReportTimezoneIana`
 * DB feature flag is on, otherwise the legacy Windows zone list. The current
 * value is always kept in the list so a stored/legacy zone still displays.
 */
export function buildReportTimezoneOptions(
  cache: Pick<InitCache, "TimeZones" | "DbFeatureFlags"> | null | undefined,
  current: string,
): TzOption[] {
  const iana = getDbFeatureFlags(cache).ReportTimezoneIana === true;
  const base: TzOption[] = iana ? IANA_ZONES.map((z) => ({ value: z, label: z })) : windowsZoneOptions(cache, current);
  return current && !base.some((o) => o.value === current)
    ? [{ value: current, label: current }, ...base]
    : base;
}
